export type OwnerShare = {
  /** The owner's percentage of the item, 0-100. */
  percentage: number;
  /** No shares were recorded, so the item was split equally between its owners. */
  isAssumed: boolean;
};

/**
 * A share to the hundredth of a percent it is captured at. Arithmetic on shares leaves float
 * noise: the raw sum of three thirds reads as 99.99000000000001%.
 */
export const roundShare = (share: number) => Math.round(share * 100) / 100;

/** Joint, with only the holder recorded: the adviser knows who holds it but not yet who with. */
export const hasUnrecordedCoOwner = (isJoint: boolean, ownerCount: number) =>
  isJoint && ownerCount < 2;

/** What the owners table and the family view's owners column call the co-owner nobody has named. */
export const UNRECORDED_CO_OWNER = 'Co-owner not recorded';

/**
 * The equal split assumed when shares are missing. The owners backfill recorded only the known
 * holder of a legacy joint item, so it is over at least two owners even where the list names one.
 */
export const evenShare = (ownerCount: number): OwnerShare => ({
  percentage: 100 / Math.max(ownerCount, 2),
  isAssumed: true,
});

/**
 * What a share of an item's value comes to. An owner's amount is only ever worked out this way:
 * the owner tables record the share, and the item records the value.
 */
export const shareOfValue = (value: number, percentage: number): number =>
  (value * percentage) / 100;

/**
 * The value an owner's share is taken of, read off a form or detected field, or null where none is
 * recorded. A completion statement gives a property's purchase price and not its value, and that
 * reads as no value, not as a value of nothing. A recorded 0 is still a value: a cleared balance.
 */
export const recordedValue = (fieldValue: unknown): number | null => {
  if (fieldValue == null || fieldValue === '') return null;
  const value = Number(fieldValue);
  return Number.isFinite(value) ? value : null;
};

/**
 * A share as the adviser reads it. An equal split between three owners is 33.333...%, so a share
 * is shown to one decimal place with a trailing zero trimmed, keeping the common 50% reading as
 * "50%". The fact find formats it the same way.
 */
export const formatOwnerShare = (share: OwnerShare): string => {
  const percentage = `${Number(share.percentage.toFixed(1))}%`;
  return share.isAssumed ? `${percentage} (assumed)` : percentage;
};

/**
 * Each recorded owner's share of an item, in the order the owners are given. A sole item is
 * wholly its owner's.
 *
 * On a joint item every owner counts, outside owners included, so a client holding 60% with
 * someone outside the family reads 60%, not the whole. The recorded percentages are trusted
 * only when every owner carries one, and are then read as weights rather than as literal
 * percentages. That keeps the owners' shares complementary: nothing yet stops a half-filled or
 * over-100 set being saved, and taking one owner's 70% at face value while the co-owner fell back
 * to an even split would attribute 120% of the item between them.
 *
 * A joint item that records only its holder has a co-owner nobody has recorded yet, and the
 * holder's typed share is read as it stands: there is no other recorded share to weigh it
 * against, so normalising it against itself would hand the holder the whole item.
 *
 * Otherwise the value splits equally between the owners, as `evenShare`.
 *
 * Not the cashflow engine's split (`FinancialRecordsSeeder._owner_shares_for`): the engine can
 * only attribute value to members of the household it models, so it leaves outside owners out,
 * and where it cannot name two members it keeps the whole value and raises a data gap. A gap is
 * no use to a table that has to put something in the cell, so the tab assumes the even split
 * and says so. The fact find reads the same rule through `owner_share` on the backend.
 */
export function ownerShares(
  recordedShares: (number | null | undefined)[],
  isJoint: boolean,
): OwnerShare[] {
  if (!isJoint) return recordedShares.map(() => ({ percentage: 100, isAssumed: false }));

  const holderShare = recordedShares[0];
  if (hasUnrecordedCoOwner(isJoint, recordedShares.length) && holderShare != null) {
    return [{ percentage: Number(holderShare), isAssumed: false }];
  }

  const total = recordedShares.reduce<number>((sum, share) => sum + Number(share ?? 0), 0);
  if (recordedShares.every(share => share != null) && total > 0) {
    return recordedShares.map(share => ({
      percentage: (Number(share) / total) * 100,
      isAssumed: false,
    }));
  }
  return recordedShares.map(() => evenShare(recordedShares.length));
}
